/* eslint-disable no-console */
// Shared-device-safe Pusher Beams client
// Implements deterministic Clear -> Set flow with a simple mutex to avoid races
// and keeps the API focused on session-level operations.

import * as PusherPushNotifications from '@pusher/push-notifications-web'
import { buildAllInterest, buildRoleInterest, buildUserInterest } from '@/libs/beams-interests'

const ROLE_INTEREST_WHITELIST = ['ROLE_DISPATCHER', 'ROLE_TEAM_LEADER']

const isEnvironmentSupported = () => {
  if (typeof window === 'undefined') return false
  if (typeof Notification === 'undefined') return false
  if (!('serviceWorker' in navigator)) return false
  if (!('PushManager' in window)) return false
  return true
}

const isDeviceMissingError = error => {
  const message = error?.message
  return typeof message === 'string' && message.includes('Device not found')
}

const buildSessionInterests = session => {
  if (!session) return []
  const interests = []
  const {
    tenantId, username, roles = [], includeAllInterest = true,
  } = session

  if (includeAllInterest) {
    const allInterest = buildAllInterest(tenantId)
    if (allInterest) {
      interests.push(allInterest)
    }
  }

  const userInterest = buildUserInterest(tenantId, username)
  if (userInterest) {
    interests.push(userInterest)
  }

  roles.forEach(role => {
    if (!ROLE_INTEREST_WHITELIST.includes(role)) {
      return
    }
    const roleInterest = buildRoleInterest(tenantId, role)
    if (roleInterest) {
      interests.push(roleInterest)
    }
  })

  return Array.from(new Set(interests))
}

const buildSessionKey = session => {
  if (!session) {
    return 'no-session'
  }
  const tenant = session.tenantId || 'no-tenant'
  const username = (session.username || 'anonymous').toLowerCase()
  const roles = Array.isArray(session.roles) ? [...session.roles] : []
  roles.sort()
  return `${tenant}::${username}::${roles.join('|')}`
}

class BeamsSessionManager {
  constructor() {
    this.instanceId = null
    this.client = null
    this.started = false
    this.queue = Promise.resolve()
    this.swReadyPromise = null
    this.currentSessionKey = null
    this.pendingSessionKey = null
    this.pendingSyncPromise = null
  }

  init(instanceId) {
    if (!instanceId) return
    if (this.instanceId && this.instanceId === instanceId) {
      return
    }
    this.instanceId = instanceId
    this.client = null
    this.started = false
    this.currentSessionKey = null
    this.pendingSessionKey = null
    this.pendingSyncPromise = null
  }

  enqueue(task) {
    const run = (this.queue || Promise.resolve()).then(() => task())
    this.queue = run.then(() => undefined).catch(err => {
      console.warn('[Beams] queued operation failed:', err?.message || err)
    })
    return run
  }

  ensureSWReady = async () => {
    if (!this.swReadyPromise) {
      this.swReadyPromise = (async () => {
        if (!('serviceWorker' in navigator)) {
          throw new Error('Service Worker not supported in this environment')
        }
        try {
          const regs = await navigator.serviceWorker.getRegistrations()
          const hasRoot = regs.some(r => new URL(r.scope).pathname === '/')
          if (!hasRoot) {
            await navigator.serviceWorker.register('/service-worker.js', { scope: '/' })
          }
        } catch (err) {
          console.warn('[Beams] SW register warning:', err?.message || err)
        }
        return navigator.serviceWorker.ready
      })()
    }
    return this.swReadyPromise
  };

  async getClient() {
    if (!this.instanceId) {
      throw new Error('Beams instanceId not set; call init() before using the client')
    }
    if (!isEnvironmentSupported()) {
      throw new Error('Beams not supported in this environment')
    }
    if (this.client) {
      return this.client
    }

    const registration = await this.ensureSWReady()
    this.client = new PusherPushNotifications.Client({
      instanceId: this.instanceId,
      serviceWorkerRegistration: registration,
    })
    this.started = false
    return this.client
  }

  async ensureStarted() {
    const client = await this.getClient()
    if (this.started) {
      return client
    }
    if (Notification.permission !== 'granted') {
      throw new Error('Notifications permission not granted')
    }
    await client.start()
    this.started = true
    console.info('[Beams] client started')
    return client
  }

  requestPermissionIfNeeded = async () => {
    if (!isEnvironmentSupported()) {
      throw new Error('Notifications are not supported in this environment')
    }
    if (Notification.permission === 'granted') return 'granted'
    if (Notification.permission === 'denied') {
      throw new Error('Notifications denied by user')
    }
    const result = await Notification.requestPermission()
    if (result !== 'granted') {
      throw new Error('Notifications not granted')
    }
    return result
  };

  async clearAllStateInternal() {
    if (!this.instanceId || !isEnvironmentSupported()) {
      return
    }
    try {
      const client = await this.getClient()
      await client.stop()
      console.info('[Beams] full state cleared for device')
    } catch (err) {
      console.warn('[Beams] clearAllState failed:', err?.message || err)
    } finally {
      this.started = false
      this.client = null
      this.currentSessionKey = null
    }
  }

  clearAllState = () => this.enqueue(() => this.clearAllStateInternal());

  async applyInterests(interests, attempt = 1) {
    try {
      const client = await this.ensureStarted()
      await client.setDeviceInterests(interests)
      console.info('[Beams] interests synced:', interests)
    } catch (err) {
      if (attempt > 0 && isDeviceMissingError(err)) {
        console.warn('[Beams] device missing, retrying setDeviceInterests')
        await this.recreateClient()
        await this.applyInterests(interests, attempt - 1)
        return
      }
      throw err
    }
  }

  async recreateClient() {
    this.client = null
    this.started = false
    await this.ensureStarted()
  }

  async syncForCurrentSession(session) {
    if (!session) {
      return Promise.resolve()
    }

    const requestedKey = buildSessionKey(session)

    if (this.currentSessionKey === requestedKey && !this.pendingSyncPromise) {
      return Promise.resolve()
    }
    if (this.pendingSessionKey === requestedKey && this.pendingSyncPromise) {
      return this.pendingSyncPromise
    }

    this.pendingSessionKey = requestedKey

    const run = this.enqueue(async () => {
      if (!this.instanceId || !isEnvironmentSupported()) {
        return
      }

      const isUserSwitch = this.currentSessionKey !== null && this.currentSessionKey !== requestedKey
      if (isUserSwitch) {
        await this.clearAllStateInternal()
      }

      const interests = buildSessionInterests(session)
      if (!interests.length) {
        this.currentSessionKey = requestedKey
        return
      }

      try {
        await this.applyInterests(interests)
        this.currentSessionKey = requestedKey
      } catch (err) {
        console.warn('[Beams] syncForCurrentSession skipped:', err?.message || err)
      }
    })

    this.pendingSyncPromise = run

    try {
      await run
    } finally {
      if (this.pendingSessionKey === requestedKey) {
        this.pendingSessionKey = null
      }
      if (this.pendingSyncPromise === run) {
        this.pendingSyncPromise = null
      }
    }

    return run
  }

  onManualLogout = () => this.clearAllState();

  onSessionExpired = () => this.clearAllState();
}

const BeamsClient = new BeamsSessionManager()
export default BeamsClient
